import { useState, useMemo, useEffect } from 'react';
import { Check, ChevronsUpDown, Plus, Tag, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';

// Subcategory options based on Category Type (from the Transaction Categorization Structure)
export const SUBCATEGORY_OPTIONS: Record<string, { value: string; label: string; description?: string }[]> = {
  revenue: [
    { value: 'deposits', label: 'Deposits', description: 'Platform deposits and income' },
    { value: 'sales_tax', label: 'Sales Tax', description: 'Sales tax collected' },
    { value: 'tax_refund', label: 'Tax Refund', description: 'Tax refund received' },
    { value: 'interest_income', label: 'Interest Income', description: 'Interest earned' },
  ],
  expenses: [
    { value: 'payroll', label: 'Payroll', description: 'Employee wages and compensation' },
    { value: 'rent', label: 'Rent', description: 'Rent and lease payments' },
    { value: 'cogs', label: 'COGS', description: 'Cost of goods sold' },
    { value: 'opex', label: 'OPEX', description: 'General operating expenses' },
    { value: 'utilities', label: 'Utilities', description: 'Electric, gas, water, etc.' },
    { value: 'insurance', label: 'Insurance', description: 'Business insurance premiums' },
    { value: 'marketing', label: 'Marketing', description: 'Marketing and advertising' },
    { value: 'software', label: 'Software', description: 'Software and SaaS subscriptions' },
    { value: 'subscriptions', label: 'Subscriptions', description: 'Recurring subscriptions and services' },
    { value: 'supplies', label: 'Supplies', description: 'Office and restaurant supplies' },
    { value: 'repairs', label: 'Repairs & Maintenance', description: 'Equipment repairs and maintenance' },
    { value: 'professional_services', label: 'Professional Services', description: 'Accounting, legal, consulting' },
    { value: 'platform_fees', label: 'Platform Fees', description: 'Delivery/payment platform fees' },
    { value: 'bank_fees', label: 'Bank & Merchant Fees', description: 'Bank charges, merchant processing' },
    { value: 'equipment', label: 'Equipment', description: 'Equipment purchases and leases' },
    { value: 'tax', label: 'Tax', description: 'Tax payments (IRS, state, local)' },
    { value: 'interest_expense', label: 'Interest Expense', description: 'Interest payments' },
    { value: 'other', label: 'Other', description: 'Other operating expenses' },
  ],
  balance_sheet: [
    { value: 'transfers', label: 'Transfers', description: 'Internal transfers' },
    { value: 'loans', label: 'Loans', description: 'Loan payments and advances' },
    { value: 'equity', label: 'Equity', description: 'Owner draws and contributions' },
  ],
};

// Detail/Source options based on Subcategory
export const DETAIL_SOURCE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  deposits: [
    { value: 'platform', label: 'Platform' },
    { value: 'cash', label: 'Cash' },
    { value: 'check', label: 'Check' },
    { value: 'wire', label: 'Wire Transfer' },
  ],
  sales_tax: [
    { value: 'collected', label: 'Collected' },
    { value: 'remitted', label: 'Remitted' },
  ],
  tax_refund: [
    { value: 'federal', label: 'Federal' },
    { value: 'state', label: 'State' },
    { value: 'local', label: 'Local' },
  ],
  interest_income: [
    { value: 'bank', label: 'Bank Interest' },
    { value: 'investment', label: 'Investment' },
  ],
  payroll: [
    { value: 'cash', label: 'Cash' },
    { value: 'w2', label: 'W2' },
    { value: 'tax', label: 'Tax' },
    { value: '1099', label: '1099' },
  ],
  rent: [
    { value: 'base', label: 'Base' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'percentage', label: '% Rent' },
    { value: 'cam', label: 'CAM' },
  ],
  cogs: [], // Vendor name - free text
  opex: [], // Manual detail - free text
  tax: [
    { value: 'federal', label: 'Federal (IRS)' },
    { value: 'state', label: 'State' },
    { value: 'local', label: 'Local' },
    { value: 'sales_tax', label: 'Sales Tax Remittance' },
    { value: 'payroll_tax', label: 'Payroll Tax' },
  ],
  interest_expense: [
    { value: 'loan', label: 'Loan Interest' },
    { value: 'credit_card', label: 'Credit Card Interest' },
    { value: 'loc', label: 'Line of Credit' },
  ],
  platform_fees: [
    { value: 'doordash', label: 'DoorDash' },
    { value: 'ubereats', label: 'Uber Eats' },
    { value: 'grubhub', label: 'Grubhub' },
    { value: 'toast', label: 'Toast' },
    { value: 'square', label: 'Square' },
    { value: 'stripe', label: 'Stripe' },
  ],
  transfers: [
    { value: 'internal', label: 'Internal' },
    { value: 'external', label: 'External' },
  ],
  loans: [
    { value: 'principal', label: 'Principal' },
    { value: 'interest', label: 'Interest' },
    { value: 'advance', label: 'Advance' },
  ],
  equity: [
    { value: 'draw', label: 'Owner Draw' },
    { value: 'contribution', label: 'Contribution' },
    { value: 'distribution', label: 'Distribution' },
  ],
};

interface SubcategoryComboboxProps {
  categoryType: 'revenue' | 'expenses' | 'balance_sheet';
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'default' | 'sm';
}

// Helper functions to manage custom subcategories in database
const loadCustomSubcategories = async (companyId: string, categoryType: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .from('custom_subcategories')
      .select('subcategory_value')
      .eq('company_id', companyId)
      .eq('category_type', categoryType)
      .order('subcategory_value', { ascending: true });

    if (error) {
      console.error('Error loading custom subcategories:', error);
      return [];
    }

    return (data || []).map(item => item.subcategory_value);
  } catch (error) {
    console.error('Error loading custom subcategories:', error);
    return [];
  }
};

const saveCustomSubcategory = async (companyId: string, categoryType: string, subcategory: string): Promise<void> => {
  try {
    const trimmedSubcategory = subcategory.trim();
    if (!trimmedSubcategory) return;

    // Use upsert to handle duplicates (UNIQUE constraint will prevent duplicates)
    const { error } = await supabase
      .from('custom_subcategories')
      .upsert({
        company_id: companyId,
        category_type: categoryType,
        subcategory_value: trimmedSubcategory.toLowerCase(),
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'company_id,category_type,subcategory_value',
      });

    if (error) {
      // Ignore duplicate key errors (they're expected)
      if (error.code !== '23505') {
        console.error('Error saving custom subcategory:', error);
      }
    }
  } catch (error) {
    console.error('Error saving custom subcategory:', error);
  }
};

export function SubcategoryCombobox({
  categoryType,
  value,
  onValueChange,
  placeholder = 'Select or type subcategory...',
  className,
  size = 'default',
}: SubcategoryComboboxProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [customSubcategories, setCustomSubcategories] = useState<string[]>([]);

  // Load custom subcategories on mount and when categoryType changes
  useEffect(() => {
    const loadCustom = async () => {
      try {
        const companyId = await getCurrentUserCompanyId();
        if (companyId) {
          const custom = await loadCustomSubcategories(companyId, categoryType);
          setCustomSubcategories(custom);
        }
      } catch (error) {
        console.error('Error loading custom subcategories:', error);
      }
    };
    loadCustom();
  }, [categoryType]);

  // Merge default options with custom subcategories
  const options = useMemo(() => {
    const defaultOpts = SUBCATEGORY_OPTIONS[categoryType] || [];
    const customOpts = customSubcategories
      .filter(custom => !defaultOpts.some(opt => opt.value.toLowerCase() === custom.toLowerCase()))
      .map(custom => ({ value: custom, label: custom }));
    return [...defaultOpts, ...customOpts];
  }, [categoryType, customSubcategories]);

  const selectedOption = options.find((opt) => opt.value.toLowerCase() === value.toLowerCase());
  const displayValue = selectedOption?.label || value;

  const handleSelect = (selectedValue: string) => {
    onValueChange(selectedValue);
    setInputValue('');
    setOpen(false);
  };

  const handleCustomAdd = async () => {
    if (inputValue.trim()) {
      const trimmedValue = inputValue.trim();
      onValueChange(trimmedValue);
      
      // Save custom subcategory to database
      try {
        const companyId = await getCurrentUserCompanyId();
        if (companyId) {
          await saveCustomSubcategory(companyId, categoryType, trimmedValue);
          // Update local state
          setCustomSubcategories(prev => {
            const normalized = trimmedValue.toLowerCase();
            if (!prev.some(s => s.toLowerCase() === normalized)) {
              return [...prev, trimmedValue];
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Error saving custom subcategory:', error);
      }
      
      setInputValue('');
      setOpen(false);
    }
  };

  // Check if current input matches an existing option
  const matchesExisting = options.some(
    (opt) => opt.value.toLowerCase() === inputValue.toLowerCase() || opt.label.toLowerCase() === inputValue.toLowerCase()
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            size === 'sm' ? 'h-8 text-sm' : 'h-10',
            !value && 'text-muted-foreground',
            !value && 'border-amber-300 bg-amber-50/50',
            className
          )}
        >
          <span className="truncate">{value ? displayValue : placeholder}</span>
          <Tag className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search or type custom..."
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            <CommandEmpty>
              {inputValue.trim() ? (
                <button
                  onClick={handleCustomAdd}
                  className="flex items-center gap-2 w-full px-2 py-3 text-sm hover:bg-accent rounded-sm cursor-pointer"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add "{inputValue.trim()}"</span>
                </button>
              ) : (
                <span className="text-muted-foreground">No options found</span>
              )}
            </CommandEmpty>
            <CommandGroup heading="Suggested">
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value.toLowerCase() === option.value.toLowerCase() ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span>{option.label}</span>
                    {option.description && (
                      <span className="text-xs text-muted-foreground">{option.description}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {inputValue.trim() && !matchesExisting && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Custom">
                  <CommandItem
                    value={`custom-${inputValue}`}
                    onSelect={handleCustomAdd}
                    className="cursor-pointer"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span>Add "{inputValue.trim()}"</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface DetailSourceComboboxProps {
  subcategory: string;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'default' | 'sm';
}

// Helper functions to manage custom detail values in database
const loadCustomDetails = async (companyId: string, subcategory: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .from('custom_detail_values')
      .select('detail_value')
      .eq('company_id', companyId)
      .eq('subcategory', subcategory.toLowerCase())
      .order('detail_value', { ascending: true });

    if (error) {
      console.error('Error loading custom details:', error);
      return [];
    }

    return (data || []).map(item => item.detail_value);
  } catch (error) {
    console.error('Error loading custom details:', error);
    return [];
  }
};

const saveCustomDetail = async (companyId: string, subcategory: string, detail: string): Promise<void> => {
  try {
    const trimmedDetail = detail.trim();
    if (!trimmedDetail) return;

    // Use upsert to handle duplicates (UNIQUE constraint will prevent duplicates)
    const { error } = await supabase
      .from('custom_detail_values')
      .upsert({
        company_id: companyId,
        subcategory: subcategory.toLowerCase(),
        detail_value: trimmedDetail,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'company_id,subcategory,detail_value',
      });

    if (error) {
      // Ignore duplicate key errors (they're expected)
      if (error.code !== '23505') {
        console.error('Error saving custom detail:', error);
      }
    }
  } catch (error) {
    console.error('Error saving custom detail:', error);
  }
};

export function DetailSourceCombobox({
  subcategory,
  value,
  onValueChange,
  placeholder = 'Select or type detail...',
  className,
  size = 'default',
}: DetailSourceComboboxProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [customDetails, setCustomDetails] = useState<string[]>([]);
  const [loadingCustomDetails, setLoadingCustomDetails] = useState(false);

  // Load custom details from database when subcategory changes
  useEffect(() => {
    const loadDetails = async () => {
      if (!subcategory) return;
      
      setLoadingCustomDetails(true);
      try {
        const companyId = await getCurrentUserCompanyId();
        if (companyId) {
          const custom = await loadCustomDetails(companyId, subcategory);
          setCustomDetails(custom);
        }
      } catch (error) {
        console.error('Error loading custom details:', error);
      } finally {
        setLoadingCustomDetails(false);
      }
    };

    loadDetails();
  }, [subcategory]);

  // Auto-save custom detail values when they're entered (not from default options)
  useEffect(() => {
    const saveDetail = async () => {
      if (value && subcategory && value.trim()) {
        const defaultOptions = DETAIL_SOURCE_OPTIONS[subcategory.toLowerCase()] || [];
        const isDefaultOption = defaultOptions.some(opt => 
          opt.value.toLowerCase() === value.trim().toLowerCase() || 
          opt.label.toLowerCase() === value.trim().toLowerCase()
        );
        
        // If it's not a default option, save it to database
        // Always save - upsert will handle duplicates via UNIQUE constraint
        if (!isDefaultOption) {
          try {
            const companyId = await getCurrentUserCompanyId();
            if (companyId) {
              // Save to database (upsert handles duplicates)
              await saveCustomDetail(companyId, subcategory, value.trim());
              
              // Reload custom details from database to ensure state is fresh
              // This ensures the dropdown is updated with the latest saved values
              const refreshed = await loadCustomDetails(companyId, subcategory);
              setCustomDetails(refreshed);
            }
          } catch (error) {
            console.error('Error auto-saving custom detail:', error);
          }
        }
      }
    };

    saveDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, subcategory]); // Only depend on value and subcategory, not customDetails to avoid loops

  // Merge default options with custom details
  const options = useMemo(() => {
    const defaultOptions = DETAIL_SOURCE_OPTIONS[subcategory.toLowerCase()] || [];
    const customOptions = customDetails
      .filter(detail => {
        // Only include custom details that aren't already in default options
        return !defaultOptions.some(opt => 
          opt.value.toLowerCase() === detail.toLowerCase() || 
          opt.label.toLowerCase() === detail.toLowerCase()
        );
      })
      .map(detail => ({ value: detail, label: detail }));
    
    return [...defaultOptions, ...customOptions];
  }, [subcategory, customDetails]);

  const selectedOption = options.find((opt) => opt.value.toLowerCase() === value.toLowerCase());
  const displayValue = selectedOption?.label || value;

  const handleSelect = (selectedValue: string) => {
    onValueChange(selectedValue);
    setInputValue('');
    setOpen(false);
  };

  const handleCustomAdd = async () => {
    if (inputValue.trim()) {
      const trimmedValue = inputValue.trim();
      try {
        const companyId = await getCurrentUserCompanyId();
        if (companyId) {
          // Save to database for future use (upsert handles duplicates)
          await saveCustomDetail(companyId, subcategory, trimmedValue);
          // Reload custom details from database to ensure state is fresh
          const refreshed = await loadCustomDetails(companyId, subcategory);
          setCustomDetails(refreshed);
        }
      } catch (error) {
        console.error('Error saving custom detail:', error);
      }
      onValueChange(trimmedValue);
      setInputValue('');
      setOpen(false);
    }
  };

  // For COGS and OPEX, always show free text input behavior
  const isFreeText = subcategory.toLowerCase() === 'cogs' || subcategory.toLowerCase() === 'opex';

  // Check if current input matches an existing option
  const matchesExisting = options.some(
    (opt) => opt.value.toLowerCase() === inputValue.toLowerCase() || opt.label.toLowerCase() === inputValue.toLowerCase()
  );

  // Check if current input matches an existing custom detail (for free text mode)
  const matchesCustomDetail = customDetails.some(
    detail => detail.toLowerCase() === inputValue.toLowerCase()
  );

  // If no default options or it's a free text subcategory, show free text input with saved suggestions
  if ((DETAIL_SOURCE_OPTIONS[subcategory.toLowerCase()]?.length === 0) || isFreeText) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between font-normal',
              size === 'sm' ? 'h-8 text-sm' : 'h-10',
              !value && 'text-muted-foreground',
              className
            )}
          >
            <span className="truncate">{value || placeholder}</span>
            <Info className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={isFreeText ? (subcategory.toLowerCase() === 'cogs' ? 'Type vendor name...' : 'Type detail...') : 'Type detail...'}
              value={inputValue}
              onValueChange={setInputValue}
            />
            <CommandList>
              {/* Show previously saved custom details as suggestions */}
              {customDetails.length > 0 && (
                <CommandGroup heading="Previously Used">
                  {customDetails
                    .filter(detail => 
                      !inputValue.trim() || 
                      detail.toLowerCase().includes(inputValue.toLowerCase())
                    )
                    .map((detail) => (
                      <CommandItem
                        key={detail}
                        value={detail}
                        onSelect={() => handleSelect(detail)}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value.toLowerCase() === detail.toLowerCase() ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <span>{detail}</span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
              {/* Show option to add new custom value if input doesn't match existing */}
              {inputValue.trim() && !matchesCustomDetail && (
                <>
                  {customDetails.length > 0 && <CommandSeparator />}
                  <CommandGroup heading="Add New">
                    <CommandItem
                      value={`custom-${inputValue}`}
                      onSelect={handleCustomAdd}
                      className="cursor-pointer"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      <span>Use "{inputValue.trim()}"</span>
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
              {/* Show empty state only when no custom details and no input */}
              {customDetails.length === 0 && !inputValue.trim() && (
                <CommandEmpty>
                  <span className="text-muted-foreground text-xs">
                    {subcategory.toLowerCase() === 'cogs' 
                      ? 'Type the vendor name' 
                      : 'Type a detail/source'}
                  </span>
                </CommandEmpty>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            size === 'sm' ? 'h-8 text-sm' : 'h-10',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <span className="truncate">{value ? displayValue : placeholder}</span>
          <Info className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search or type custom..."
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            <CommandEmpty>
              {inputValue.trim() ? (
                <button
                  onClick={handleCustomAdd}
                  className="flex items-center gap-2 w-full px-2 py-3 text-sm hover:bg-accent rounded-sm cursor-pointer"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add "{inputValue.trim()}"</span>
                </button>
              ) : (
                <span className="text-muted-foreground">No options found</span>
              )}
            </CommandEmpty>
            <CommandGroup heading="Suggested">
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value.toLowerCase() === option.value.toLowerCase() ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span>{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {inputValue.trim() && !matchesExisting && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Custom">
                  <CommandItem
                    value={`custom-${inputValue}`}
                    onSelect={handleCustomAdd}
                    className="cursor-pointer"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span>Add "{inputValue.trim()}"</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

